import { useEffect, useMemo, useState } from "react";
import { getTrades, updateTrade } from "./StockTradeService";
import { getTradingAccounts } from "./TradingAccountService";

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const toDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const buildGroupPnlMap = (trades) => {
  const map = new Map();
  trades.forEach((t) => {
    const key = `${t.tradingAccountId || ""}::${t.symbol || ""}`;
    if (!map.has(key)) {
      map.set(key, { buyQty: 0, buyValue: 0, sellQty: 0, sellValue: 0, fees: 0, taxes: 0 });
    }
    const bucket = map.get(key);
    const qty = toNumber(t.quantity);
    const price = toNumber(t.price);
    const amount = qty * price;
    if (t.tradeType === "SELL") {
      bucket.sellQty += qty;
      bucket.sellValue += amount;
    } else {
      bucket.buyQty += qty;
      bucket.buyValue += amount;
    }
    bucket.fees += toNumber(t.fees);
    bucket.taxes += toNumber(t.taxes);
  });
  const pnlMap = new Map();
  map.forEach((bucket, key) => {
    const avgBuy = bucket.buyQty === 0 ? 0 : bucket.buyValue / bucket.buyQty;
    const realizedPnl = bucket.sellValue - avgBuy * bucket.sellQty - bucket.fees - bucket.taxes;
    pnlMap.set(key, realizedPnl);
  });
  return pnlMap;
};

export default function StockJournal() {
  const [trades, setTrades] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [filters, setFilters] = useState({
    accountId: "",
    symbol: "",
    type: "",
    notesOnly: false,
    search: "",
  });
  const [editId, setEditId] = useState(null);
  const [editNotes, setEditNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    const [tradeData, accountData] = await Promise.all([getTrades(), getTradingAccounts()]);
    setTrades(Array.isArray(tradeData) ? tradeData : []);
    setAccounts(Array.isArray(accountData) ? accountData : []);
  };

  const accountLabelById = useMemo(() => {
    const map = new Map();
    accounts.forEach((a) => {
      map.set(a.id, `${a.brokerName} (${a.accountNumber || a.id})`);
    });
    return map;
  }, [accounts]);

  const filtered = useMemo(() => {
    const query = filters.search.trim().toLowerCase();
    return trades.filter((t) => {
      if (filters.accountId && t.tradingAccountId !== filters.accountId) return false;
      if (filters.type && t.tradeType !== filters.type) return false;
      if (filters.symbol && (t.symbol || "").toLowerCase() !== filters.symbol.trim().toLowerCase()) return false;
      const note = (t.notes || "").trim();
      if (filters.notesOnly && !note) return false;
      if (query) {
        const haystack = `${t.symbol || ""} ${t.stockName || ""} ${note}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [trades, filters]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      const da = toDate(a.tradeDate);
      const db = toDate(b.tradeDate);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return db.getTime() - da.getTime();
    });
    return copy;
  }, [filtered]);

  const notesCount = useMemo(() => trades.filter((t) => (t.notes || "").trim().length > 0).length, [trades]);
  const pnlMap = useMemo(() => buildGroupPnlMap(trades), [trades]);

  const startEdit = (trade) => {
    setEditId(trade.id);
    setEditNotes(trade.notes || "");
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditNotes("");
  };

  const saveEdit = async (trade) => {
    if (!trade?.id) return;
    setIsSaving(true);
    try {
      await updateTrade(trade.id, { notes: editNotes || "" });
      setTrades((prev) =>
        prev.map((t) => (t.id === trade.id ? { ...t, notes: editNotes || "" } : t))
      );
      cancelEdit();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="stocks-section">
      <div className="card">
        <div className="card-title">Trade Journal</div>
        <div className="stocks-journal-toolbar">
          <select
            className="input"
            value={filters.accountId}
            onChange={(e) => setFilters((prev) => ({ ...prev, accountId: e.target.value }))}
          >
            <option value="">All accounts</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.brokerName} ({a.accountNumber || a.id})
              </option>
            ))}
          </select>
          <input
            className="input"
            value={filters.symbol}
            onChange={(e) => setFilters((prev) => ({ ...prev, symbol: e.target.value }))}
            placeholder="Symbol"
          />
          <select
            className="input"
            value={filters.type}
            onChange={(e) => setFilters((prev) => ({ ...prev, type: e.target.value }))}
          >
            <option value="">All types</option>
            <option value="BUY">BUY</option>
            <option value="SELL">SELL</option>
          </select>
          <input
            className="input"
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            placeholder="Search notes, symbol"
          />
          <label className="inline-checkbox">
            <input
              type="checkbox"
              checked={filters.notesOnly}
              onChange={(e) => setFilters((prev) => ({ ...prev, notesOnly: e.target.checked }))}
            />
            Notes only
          </label>
        </div>
        <div className="stocks-journal-meta">
          {sorted.length} trades · {notesCount} journaled
        </div>
      </div>

      <div className="stocks-journal-list">
        {sorted.map((t) => {
          const pnlKey = `${t.tradingAccountId || ""}::${t.symbol || ""}`;
          const pnl = pnlMap.get(pnlKey) ?? 0;
          const amount = toNumber(t.quantity) * toNumber(t.price);
          const isEditing = editId === t.id;
          return (
            <div key={t.id} className="stocks-journal-card">
              <div className="stocks-journal-header">
                <div className="stocks-journal-title">
                  <span className={`stocks-pill ${t.tradeType === "SELL" ? "stocks-pill-sell" : "stocks-pill-buy"}`}>
                    {t.tradeType}
                  </span>
                  {t.strategyTag && <span className="stocks-pill stocks-pill-strategy">{t.strategyTag}</span>}
                  <span className="stocks-journal-symbol">{t.symbol}</span>
                  {t.stockName && <span className="stocks-journal-name">{t.stockName}</span>}
                </div>
                <div className="stocks-journal-header-meta">
                  <span>{t.tradeDate || "--"}</span>
                  <span>{accountLabelById.get(t.tradingAccountId) || t.tradingAccountId}</span>
                </div>
              </div>
              <div className="stocks-journal-stats">
                <span>Qty {t.quantity}</span>
                <span>Price {t.price}</span>
                <span>Amount {amount.toFixed(2)}</span>
                <span className={pnl >= 0 ? "stocks-good" : "stocks-bad"}>Stock PnL {pnl.toFixed(2)}</span>
              </div>
              <div className="stocks-journal-note">
                {isEditing ? (
                  <textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Add trade notes, thesis, risk, execution, lesson learned..."
                  />
                ) : (
                  <div className={t.notes ? "stocks-journal-note-text" : "stocks-journal-note-empty"}>
                    {t.notes || "No journal notes yet."}
                  </div>
                )}
              </div>
              <div className="row-actions">
                {!isEditing && (
                  <button className="btn btn-secondary" onClick={() => startEdit(t)}>
                    {t.notes ? "Edit Notes" : "Add Notes"}
                  </button>
                )}
                {isEditing && (
                  <>
                    <button className="btn btn-primary" onClick={() => saveEdit(t)} disabled={isSaving}>
                      {isSaving ? "Saving..." : "Save Notes"}
                    </button>
                    <button className="btn btn-secondary" onClick={cancelEdit} disabled={isSaving}>
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
        {sorted.length === 0 && <div className="stocks-empty">No trades match the journal filters.</div>}
      </div>
    </div>
  );
}
